import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRumError, PIIRedactor } from 'services/observability/datadog';
import { Toast, TOAST_VARIANTS } from 'ui/toast';
import { reactNodeToString } from 'utils/reactHelpers';
import { StyledToastContainer } from './ToastContainer.styled';
import { selectToastsAsList } from '../selectors';
import { useToastsContainerLocation } from '../hooks/useToastsContainerLocation';
import { removeToastByKeyRequest } from '../actions';
import type { ToastDataType } from '../types';

const reportedErrors: string[] = [];

export const ToastContainer = () => {
  const dispatch = useDispatch();

  const toasts = useSelector(selectToastsAsList);
  const toastsLocation = useToastsContainerLocation();

  const addErrorSignal = useRumError();

  useEffect(() => {
    toasts.forEach(({ key, variant, errorIdentifier = {}, children }) => {
      if (!reportedErrors.includes(key) && variant === TOAST_VARIANTS.NEGATIVE) {
        addErrorSignal(
          new Error(PIIRedactor.fromString(reactNodeToString(children))),
          'TOAST_ERROR',
          { ...errorIdentifier },
        );
        reportedErrors.push(key);
      }
    });
  }, [toasts?.length, toasts, addErrorSignal]);

  const removeToastByKey = useCallback(
    (key: ToastDataType['key']) => {
      dispatch(removeToastByKeyRequest({ key }));
    },
    [dispatch],
  );

  return (
    <StyledToastContainer data-toasts-location={toastsLocation} data-test="toast-container">
      {toasts.map(({ key, ...toast }) => (
        <Toast key={key} closeToast={() => removeToastByKey(key)} {...toast} />
      ))}
    </StyledToastContainer>
  );
};

export default ToastContainer;
